/**
 * PlanScene — the DISPLAY LIST between the geometry domain and any
 * renderer (Canvas 2D today; SVG export / WebGL / print later).
 *
 * Pure: world coordinates only, semantic style tokens instead of colours,
 * screen-space sizes expressed in px hints the renderer applies after the
 * viewport transform. No DOM/canvas/React here (enforced by test).
 */
import type { PlanAnalysis, WallRun } from "./analysis";
import type { PlanModel, PlanObject, Point, Wall, WallOpening } from "./model";
import { activeObjects, activeOpenings, activeWalls, nodeOf, windowUnitCount } from "./model";
import { objectOverlapsWall, objectPose } from "./objects";
import { objectSymbol } from "./symbols";
import type { SnapResult } from "./snapping";
import { formatLength, PU_PER_FOOT } from "./units";
import { direction, distance, leftNormal, pointAlong, roundPoint } from "./vector";

export type StyleToken =
  | "ghost"
  | "change-removed"
  | "change-added"
  | "background"
  | "grid-minor"
  | "grid-major"
  | "region"
  | "region-unassigned"
  | "region-conflict"
  | "region-selected"
  | "region-hover"
  | "wall"
  | "wall-exterior"
  | "wall-selected"
  | "wall-hover"
  | "wall-preview"
  | "boundary"
  | "boundary-selected"
  | "boundary-hover"
  | "boundary-preview"
  | "opening-gap"
  | "opening-symbol"
  | "opening-selected"
  | "label"
  | "label-strong"
  | "handle-pinned"
  | "handle-moving"
  | "handle"
  | "snap"
  | "guide"
  | "gap"
  | "anchor"
  | "object"
  | "object-selected"
  | "object-hover"
  | "object-warning"
  | "object-preview"
  | "object-fill"
  | "object-counter"
  | "object-label"
  /* Spec §83: the annotation layer — presentation, never geometry. */
  | "annotation"
  | "dimension"
  | "dimension-unresolved";

export type ScenePrimitive =
  | { type: "polygon"; points: Point[]; style: StyleToken; id?: string }
  | { type: "line"; from: Point; to: Point; style: StyleToken; widthPx: number; dash?: number[] }
  | { type: "arc"; center: Point; radiusPu: number; startRad: number; endRad: number; ccw: boolean; style: StyleToken; widthPx: number }
  | { type: "marker"; at: Point; radiusPx: number; style: StyleToken; shape: "circle" | "square" | "ring" }
  | { type: "text"; at: Point; text: string; style: StyleToken; sizePx: number; offsetPx?: [number, number]; angleRad?: number };

export interface PlanScene {
  /** Re-rendered on model / viewport changes. */
  static: ScenePrimitive[];
  /** Re-rendered on every pointer move (preview, snap, handles). */
  overlay: ScenePrimitive[];
  /** World-space bounding box of active geometry (for zoom-to-fit). */
  bounds: { min: Point; max: Point } | null;
}

export interface SceneSelection {
  wallId?: string | null;
  openingId?: string | null;
  objectId?: string | null;
  /** Extra objects to flash/highlight (Show in Design). */
  highlightObjectIds?: ReadonlySet<string> | null;
  regionKey?: string | null;
  roomId?: string | null;
  /** Which end of the selected wall is pinned for length edits. */
  pinnedEnd?: "start" | "end";
}

export interface SceneState {
  selection: SceneSelection;
  hover: { wallId?: string | null; regionKey?: string | null; openingId?: string | null; objectId?: string | null };
  /** Transient model during drags/drawing (replaces `model` for static layer when set). */
  preview?: PlanModel | null;
  previewWall?: { from: Point; to: Point; thickness: number; kind?: "wall" | "boundary" } | null;
  previewRect?: { corner: Point; other: Point } | null;
  previewOpening?: {
    wallId: string;
    offsetFromStart: number;
    width: number;
    kind: WallOpening["kind"];
    swing?: WallOpening["swing"];
    windowConfig?: WallOpening["windowConfig"];
  } | null;
  /** Ghost object following the cursor during placement. */
  previewObject?: PlanObject | null;
  previewObjectSymbol?: string | null;
  snap?: SnapResult | null;
  /** px per pu — used to decide label density. */
  scale: number;
  showLabels: boolean;
  /** Show logical room boundaries (dashed); default true in the editor. */
  showBoundaries?: boolean;
  /** definitionId → symbol key + visibility category (from object_definitions). */
  objectMeta?: ReadonlyMap<string, { symbolKey: string; category: string }>;
  /**
   * Stair figures per object id (spec §60I): how many steps to draw and
   * which way UP points. Absent while nobody has measured — the symbol then
   * draws a readable default rather than inventing a step count.
   */
  stairs?: ReadonlyMap<string, { stepCount: number | null; upDirection: "forward" | "back" }>;
  /** Visible object categories; undefined = show all. */
  visibleCategories?: ReadonlySet<string> | null;
  /** Room names for bound regions (roomId → name). */
  roomNames?: ReadonlyMap<string, string>;
}

export function wallPolygon(model: PlanModel, w: Wall, extendEnds = true): Point[] {
  const a = nodeOf(model, w.startNodeId);
  const b = nodeOf(model, w.endNodeId);
  return segmentPolygon(a, b, w.thickness, extendEnds ? nodeDegree(model, w.startNodeId) > 1 : false, extendEnds ? nodeDegree(model, w.endNodeId) > 1 : false);
}

function nodeDegree(model: PlanModel, nodeId: string): number {
  let n = 0;
  for (const w of model.walls.values()) if (w.archivedAt === null && (w.startNodeId === nodeId || w.endNodeId === nodeId)) n++;
  return n;
}

/** Rectangle around a→b with thickness; optionally extended by half thickness at each end (fills joints). */
export function segmentPolygon(a: Point, b: Point, thickness: number, extendA: boolean, extendB: boolean): Point[] {
  const d = direction(a, b);
  const n = leftNormal(a, b);
  const h = thickness / 2;
  const ea = extendA ? h : 0;
  const eb = extendB ? h : 0;
  return [
    roundPoint({ x: a.x - d.x * ea + n.x * h, y: a.y - d.y * ea + n.y * h }),
    roundPoint({ x: b.x + d.x * eb + n.x * h, y: b.y + d.y * eb + n.y * h }),
    roundPoint({ x: b.x + d.x * eb - n.x * h, y: b.y + d.y * eb - n.y * h }),
    roundPoint({ x: a.x - d.x * ea - n.x * h, y: a.y - d.y * ea - n.y * h }),
  ];
}

function openingPrimitives(
  model: PlanModel,
  w: Wall,
  o: Pick<WallOpening, "kind" | "offsetFromStart" | "width" | "swing"> & { windowConfig?: WallOpening["windowConfig"] },
  selected: boolean,
): ScenePrimitive[] {
  const a = nodeOf(model, w.startNodeId);
  const b = nodeOf(model, w.endNodeId);
  const s = pointAlong(a, b, o.offsetFromStart);
  const e = pointAlong(a, b, o.offsetFromStart + o.width);
  const prims: ScenePrimitive[] = [];
  prims.push({ type: "polygon", points: segmentPolygon(s, e, w.thickness + 2, false, false), style: "opening-gap" });
  const style: StyleToken = selected ? "opening-selected" : "opening-symbol";
  const n = leftNormal(a, b);
  const h = w.thickness / 2;
  if (o.kind === "window") {
    // Jambs, the glass line, and a MULLION between each pair of sashes: a
    // double window is drawn as a double window, not as a single one with a
    // note attached to it (spec §60G).
    prims.push({ type: "line", from: roundPoint({ x: s.x + n.x * h, y: s.y + n.y * h }), to: roundPoint({ x: s.x - n.x * h, y: s.y - n.y * h }), style, widthPx: 1.5 });
    prims.push({ type: "line", from: roundPoint({ x: e.x + n.x * h, y: e.y + n.y * h }), to: roundPoint({ x: e.x - n.x * h, y: e.y - n.y * h }), style, widthPx: 1.5 });
    prims.push({ type: "line", from: s, to: e, style, widthPx: 1.5 });
    const units = windowUnitCount(o.windowConfig);
    for (let i = 1; i < units; i += 1) {
      const at = pointAlong(s, e, (o.width * i) / units);
      prims.push({
        type: "line",
        from: roundPoint({ x: at.x + n.x * h, y: at.y + n.y * h }),
        to: roundPoint({ x: at.x - n.x * h, y: at.y - n.y * h }),
        style,
        widthPx: 1.5,
      });
    }
  } else if (o.kind === "door") {
    // Conventional plan symbol: the door leaf drawn OPEN (perpendicular to
    // the wall on the swing side) plus a QUARTER-CIRCLE arc tracing the
    // leaf path from closed (lying in the doorway) to open — never more.
    const swing = o.swing ?? "left-in";
    const hinge = swing.startsWith("left") ? s : e;
    const free = swing.startsWith("left") ? e : s;
    const side = swing.endsWith("in") ? 1 : -1;
    const base = roundPoint({ x: hinge.x + n.x * h * side, y: hinge.y + n.y * h * side });
    const leaf = roundPoint({ x: base.x + n.x * o.width * side, y: base.y + n.y * o.width * side });
    prims.push({ type: "line", from: base, to: leaf, style, widthPx: 1.5 });
    const dirHF = direction(hinge, free);
    const startAngle = Math.atan2(dirHF.y, dirHF.x); // closed position (leaf in the doorway)
    const endAngle = Math.atan2(n.y * side, n.x * side); // open position (perpendicular)
    // Sweep the SHORT way (90°): world-CCW iff closed × open is positive.
    const crossZ = dirHF.x * (n.y * side) - dirHF.y * (n.x * side);
    prims.push({ type: "arc", center: base, radiusPu: o.width, startRad: startAngle, endRad: endAngle, ccw: crossZ > 0, style, widthPx: 1 });
    prims.push({ type: "line", from: roundPoint({ x: s.x + n.x * h, y: s.y + n.y * h }), to: roundPoint({ x: s.x - n.x * h, y: s.y - n.y * h }), style, widthPx: 1 });
    prims.push({ type: "line", from: roundPoint({ x: e.x + n.x * h, y: e.y + n.y * h }), to: roundPoint({ x: e.x - n.x * h, y: e.y - n.y * h }), style, widthPx: 1 });
  } else {
    prims.push({ type: "line", from: s, to: e, style, widthPx: 1, dash: [4, 3] });
    prims.push({ type: "line", from: roundPoint({ x: s.x + n.x * h, y: s.y + n.y * h }), to: roundPoint({ x: s.x - n.x * h, y: s.y - n.y * h }), style, widthPx: 1 });
    prims.push({ type: "line", from: roundPoint({ x: e.x + n.x * h, y: e.y + n.y * h }), to: roundPoint({ x: e.x - n.x * h, y: e.y - n.y * h }), style, widthPx: 1 });
  }
  return prims;
}

/**
 * The two clear distances beside a selected opening: wall end → opening
 * edge, and opening edge → wall end. Drawn just off the wall, on the same
 * side as the wall's own dimension, and only when there is room to read
 * them (spec §61L).
 */
function openingOffsetPrimitives(model: PlanModel, wall: Wall, opening: WallOpening, scale: number): ScenePrimitive[] {
  const a = nodeOf(model, wall.startNodeId);
  const b = nodeOf(model, wall.endNodeId);
  const length = Math.hypot(b.x - a.x, b.y - a.y);
  if (length <= 0) return [];
  const ux = (b.x - a.x) / length;
  const uy = (b.y - a.y) / length;
  const nx = -uy;
  const ny = ux;
  const off = wall.thickness / 2 + 18 / scale;
  const at = (d: number) => roundPoint({ x: a.x + ux * d + nx * off, y: a.y + uy * d + ny * off });
  const angle = Math.atan2(b.y - a.y, b.x - a.x);
  const prims: ScenePrimitive[] = [];

  const spans: Array<[number, number]> = [
    [0, opening.offsetFromStart],
    [opening.offsetFromStart + opening.width, length],
  ];
  for (const [from, to] of spans) {
    const span = to - from;
    if (span <= 0) continue;
    const p0 = at(from);
    const p1 = at(to);
    prims.push({ type: "line", from: p0, to: p1, style: "guide", widthPx: 1 });
    // ticks at both ends, so it reads as a dimension rather than a stray line
    for (const point of [p0, p1]) {
      prims.push({
        type: "line",
        from: roundPoint({ x: point.x - nx * (5 / scale), y: point.y - ny * (5 / scale) }),
        to: roundPoint({ x: point.x + nx * (5 / scale), y: point.y + ny * (5 / scale) }),
        style: "guide",
        widthPx: 1,
      });
    }
    if (span * scale >= 30) {
      prims.push({
        type: "text",
        at: at(from + span / 2),
        text: formatLength(Math.round(span)),
        style: "label",
        sizePx: 10,
        angleRad: -angle,
      });
    }
  }
  return prims;
}

function wallLabel(model: PlanModel, w: Wall, run: WallRun | undefined, runsLabelled: Set<string>, scale: number): ScenePrimitive[] {
  const a = nodeOf(model, w.startNodeId);
  const b = nodeOf(model, w.endNodeId);
  const len = Math.round(distance(a, b));
  const mid = roundPoint({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });
  const n = leftNormal(a, b);
  const off = w.thickness / 2 + 6 / scale;
  const d = direction(a, b);
  let angle = Math.atan2(d.y, d.x);
  if (angle > Math.PI / 2 || angle < -Math.PI / 2) angle += Math.PI; // keep text upright
  const prims: ScenePrimitive[] = [];
  // label only when the wall is long enough on screen
  if (len * scale >= 44) {
    prims.push({ type: "text", at: roundPoint({ x: mid.x + n.x * off, y: mid.y + n.y * off }), text: formatLength(len), style: "label", sizePx: 11, angleRad: -angle });
  }
  if (run && run.wallIds.length > 1 && !runsLabelled.has(run.id)) {
    runsLabelled.add(run.id);
    const s = nodeOf(model, run.startNodeId);
    const e = nodeOf(model, run.endNodeId);
    if (run.lengthPu * scale >= 80) {
      const rm = roundPoint({ x: (s.x + e.x) / 2, y: (s.y + e.y) / 2 });
      const rn = leftNormal(s, e);
      const roff = w.thickness / 2 + 22 / scale;
      prims.push({ type: "text", at: roundPoint({ x: rm.x - rn.x * roff, y: rm.y - rn.y * roff }), text: `⟷ ${formatLength(run.lengthPu)}`, style: "label-strong", sizePx: 11, angleRad: -angle });
    }
  }
  return prims;
}

export function buildScene(model: PlanModel, analysis: PlanAnalysis, state: SceneState): PlanScene {
  const m = state.preview ?? model;
  const statics: ScenePrimitive[] = [];
  const overlay: ScenePrimitive[] = [];
  // regions
  for (const r of analysis.regions) {
    const selected = state.selection.regionKey === r.key || (state.selection.roomId != null && r.roomIds.includes(state.selection.roomId));
    const hover = state.hover.regionKey === r.key;
    const style: StyleToken = selected ? "region-selected" : hover ? "region-hover" : r.status === "conflict" ? "region-conflict" : r.status === "unassigned" ? "region-unassigned" : "region";
    statics.push({ type: "polygon", points: r.interior.length >= 3 ? r.interior : r.centerline, style, id: r.key });
    if (state.showLabels) {
      const names = r.roomIds.map((id) => state.roomNames?.get(id) ?? "Room");
      const title = r.status === "unassigned" ? "Unassigned space" : r.status === "conflict" ? `Conflict: ${names.join(" + ")}` : names[0];
      // Always inside the room's own polygon (spec §60B): a centroid lands
      // outside an L-shaped room, which is how a name ended up next door.
      statics.push({ type: "text", at: r.labelPoint, text: title, style: "label-strong", sizePx: 12, offsetPx: [0, -7] });
      statics.push({ type: "text", at: r.labelPoint, text: `${(r.floorAreaX100 / 100).toFixed(r.floorAreaX100 % 100 === 0 ? 0 : 1)} sqft`, style: "label", sizePx: 11, offsetPx: [0, 8] });
    }
  }
  // walls
  const runsLabelled = new Set<string>();
  for (const w of activeWalls(m)) {
    const selected = state.selection.wallId === w.id;
    const hover = state.hover.wallId === w.id;
    if (w.kind === "boundary") {
      // Logical room boundary: dashed editor aid, never a building component.
      if (state.showBoundaries === false && !selected && !hover) continue;
      const a = nodeOf(m, w.startNodeId);
      const b = nodeOf(m, w.endNodeId);
      const style: StyleToken = selected ? "boundary-selected" : hover ? "boundary-hover" : "boundary";
      statics.push({ type: "line", from: a, to: b, style, widthPx: selected || hover ? 3 : 2, dash: [7, 5] });
      continue;
    }
    const style: StyleToken = selected ? "wall-selected" : hover ? "wall-hover" : analysis.exteriorWallIds.has(w.id) ? "wall-exterior" : "wall";
    statics.push({ type: "polygon", points: wallPolygon(m, w), style, id: w.id });
  }
  for (const o of activeOpenings(m)) {
    const w = m.walls.get(o.wallId);
    if (!w || w.archivedAt !== null) continue;
    const active = state.selection.openingId === o.id || state.hover.openingId === o.id;
    statics.push(...openingPrimitives(m, w, o, active));
    // Spec §61L: how far it is from each end of its wall, measured from the
    // EDGE of the opening. Only for the one being worked on — every door in
    // the house carrying two dimensions would be unreadable.
    if (active) statics.push(...openingOffsetPrimitives(m, w, o, state.scale));
  }
  // plan objects (Phase 8): symbol per definition, category visibility
  for (const o of activeObjects(m)) {
    const meta = state.objectMeta?.get(o.definitionId);
    if (state.visibleCategories && meta && !state.visibleCategories.has(meta.category)) continue;
    const selected = state.selection.objectId === o.id || state.selection.highlightObjectIds?.has(o.id);
    const hover = state.hover.objectId === o.id;
    const style: StyleToken = selected ? "object-selected" : hover ? "object-hover" : objectOverlapsWall(m, o) ? "object-warning" : "object";
    const stair = state.stairs?.get(o.id);
    statics.push(
      ...objectSymbol(
        m,
        stair ? { ...o, stepCount: stair.stepCount, upDirection: stair.upDirection } : o,
        meta?.symbolKey ?? "generic",
        style,
      ),
    );
    if (selected) {
      statics.push({ type: "marker", at: objectPose(m, o).center, radiusPx: 4, style: "handle", shape: "circle" });
    }
    // A cabinet's WIDTH is what somebody buys it by (spec §61H), so it is
    // drawn on the module rather than hidden in the Inspector — but only
    // when the drawing is zoomed enough to read it, or the module is the one
    // being worked on. Everything else stays quiet.
    if (meta?.category === "cabinet" && o.geometryType === "point") {
      const readable = state.scale !== undefined && o.width * state.scale >= 34;
      if (selected || hover || readable) {
        statics.push({
          type: "text",
          at: objectPose(m, o).center,
          text: formatLength(o.width),
          style: "object-label",
          sizePx: 10,
        });
      }
    }
  }
  if (state.showLabels) {
    for (const w of activeWalls(m)) {
      if (w.kind === "boundary") continue;
      statics.push(...wallLabel(m, w, analysis.runByWall.get(w.id), runsLabelled, state.scale));
    }
  }
  // gaps
  for (const g of analysis.gaps) {
    const n = m.nodes.get(g.nodeId);
    if (n) statics.push({ type: "marker", at: n, radiusPx: 7, style: "gap", shape: "ring" });
  }
  // anchors
  // The anchor marker is shown only for the SELECTED room: it is a way to
  // nudge a label, never the way to identify a room (spec §60B).
  for (const a of m.anchors.values()) {
    if (state.selection.roomId !== a.roomId) continue;
    statics.push({ type: "marker", at: a, radiusPx: 4, style: "anchor", shape: "square" });
  }

  // overlay: selected wall handles
  if (state.selection.wallId) {
    const w = m.walls.get(state.selection.wallId);
    if (w && w.archivedAt === null) {
      const a = nodeOf(m, w.startNodeId);
      const b = nodeOf(m, w.endNodeId);
      const pinned = state.selection.pinnedEnd ?? "start";
      overlay.push({ type: "marker", at: a, radiusPx: 6, style: pinned === "start" ? "handle-pinned" : "handle-moving", shape: "circle" });
      overlay.push({ type: "marker", at: b, radiusPx: 6, style: pinned === "end" ? "handle-pinned" : "handle-moving", shape: "circle" });
      overlay.push({ type: "text", at: pinned === "start" ? a : b, text: "PINNED", style: "label-strong", sizePx: 9, offsetPx: [0, -12] });
    }
  }
  if (state.previewWall) {
    const { from, to, thickness } = state.previewWall;
    if (!(from.x === to.x && from.y === to.y)) {
      if (state.previewWall.kind === "boundary") overlay.push({ type: "line", from, to, style: "boundary-preview", widthPx: 2, dash: [7, 5] });
      else overlay.push({ type: "polygon", points: segmentPolygon(from, to, thickness, false, false), style: "wall-preview" });
      const len = Math.round(distance(from, to));
      const mid = roundPoint({ x: (from.x + to.x) / 2, y: (from.y + to.y) / 2 });
      overlay.push({ type: "text", at: mid, text: formatLength(len), style: "label-strong", sizePx: 12, offsetPx: [0, -14] });
    }
  }
  if (state.previewRect) {
    const { corner, other } = state.previewRect;
    const pts = [corner, { x: other.x, y: corner.y }, other, { x: corner.x, y: other.y }];
    overlay.push({ type: "polygon", points: pts, style: "region-hover" });
    for (let i = 0; i < 4; i++) overlay.push({ type: "line", from: pts[i], to: pts[(i + 1) % 4], style: "wall-preview", widthPx: 2, dash: [6, 4] });
    overlay.push({ type: "text", at: roundPoint({ x: (corner.x + other.x) / 2, y: (corner.y + other.y) / 2 }), text: `${formatLength(Math.abs(other.x - corner.x))} × ${formatLength(Math.abs(other.y - corner.y))}`, style: "label-strong", sizePx: 12 });
  }
  if (state.previewOpening) {
    const w = m.walls.get(state.previewOpening.wallId);
    // The preview shows the swing the drag is currently describing, so the
    // leaf and its arc follow the pointer (spec §60F).
    if (w && w.archivedAt === null) {
      overlay.push(...openingPrimitives(m, w, { swing: "left-in", ...state.previewOpening }, true));
    }
  }
  if (state.previewObject) {
    overlay.push(...objectSymbol(m, state.previewObject, state.previewObjectSymbol ?? "generic", "object-preview"));
  }
  if (state.snap && state.snap.kind) {
    for (const g of state.snap.guides) overlay.push({ type: "line", from: g.from, to: g.to, style: "guide", widthPx: 1, dash: [4, 4] });
    overlay.push({ type: "marker", at: state.snap.point, radiusPx: 7, style: "snap", shape: state.snap.kind === "endpoint" ? "circle" : "ring" });
    if (state.snap.label) overlay.push({ type: "text", at: state.snap.point, text: state.snap.label, style: "snap", sizePx: 11, offsetPx: [12, -12] });
  }
  return { static: statics, overlay, bounds: bounds(m) };
}

export function bounds(model: PlanModel): { min: Point; max: Point } | null {
  let min: Point | null = null;
  let max: Point | null = null;
  for (const n of model.nodes.values()) {
    if (n.archivedAt !== null) continue;
    min = min ? { x: Math.min(min.x, n.x), y: Math.min(min.y, n.y) } : { x: n.x, y: n.y };
    max = max ? { x: Math.max(max.x, n.x), y: Math.max(max.y, n.y) } : { x: n.x, y: n.y };
  }
  if (!min || !max) return null;
  const pad = PU_PER_FOOT * 2;
  return { min: { x: min.x - pad, y: min.y - pad }, max: { x: max.x + pad, y: max.y + pad } };
}
